import { Emitter } from './emitter';

export const StorageEvents = Object.freeze({
    clear: 'clear',
    clearExpired: 'clearExpired',
    changed: 'changed',
    delete: 'delete',
    update: 'update',
    load: 'load',
    stop: 'stop',
    start: 'start',
});

/**
 * 本地存储器
 */
class Storage {
    static instances = new Map();

    constructor (options = {
        storeKey: 'icloud-caches',
        clearInterval: 10 * 60 * 60 * 1000, // 10分种
        store: localStorage,
    }) {
        const storeKey = options.storeKey || 'icloud-caches';
        if (Storage.instances.has(storeKey)) {
            return Storage.instances.get(storeKey);
        }
        this._options = { ...options, storeKey };
        this._emitter = new Emitter({}, 'local-storage-cache');
        this._allKeys = new Map();
        this._keysChanged = false; // 键名是否有变动
        this._storage = options.store || localStorage;
        this._clearTimer = 0; // 定期清理定时器

        this._emit = (event, ...args) => {
            this._emitter.emit(event, ...args);
        };

        this._on = (event, listener) => {
            return this._emitter.on(event, listener);
        };

        this._once = (event, listener) => {
            this._emitter.once(event, listener);
        };

        this._off = (event, listener) => {
            this._emitter.off(event, listener);
        };

        this._getAllKeyStoreKey = () => {
            return `${storeKey}--keys`;
        };

        this._getDataStoreKey = (key) => {
            return `${storeKey}-#${key}`;
        };

        this._removes = (keys) => {
            let count = 0;
            for (const key of keys) {
                const dataStoreKey = this._getDataStoreKey(key);
                this._storage.removeItem(dataStoreKey);
                if (this._allKeys.has(key)) {
                    this._allKeys.delete(key);
                    count++;
                }
                this._emit(StorageEvents.delete, { key });
            }
            return count;
        };

        // 保存键名
        this._saveKeys = () => {
            if (!this._keysChanged) return;
            try {
                this._keysChanged = false;
                const s = JSON.stringify(Object.fromEntries(this._allKeys));
                this._storage.setItem(this._getAllKeyStoreKey(), s);
            } catch (e) {
                console.error('[Storage] saveKeys error:', e);
            }
        };

        // private clearExpiredKeys 清理过期的键
        this._clearExpiredKeys = () => {
            const nowTime = (new Date()).getTime();
            const delKeys = new Set();
            Array.from(this._allKeys).forEach(([ k, v ]) => {
                if (v.expire < nowTime) {
                    delKeys.add(k);
                    this._allKeys.delete(k);
                }
            });
            if (delKeys.size > 0) {
                const keys = [ ...Array.from(delKeys) ];
                const count = this._removes(keys);
                this._emit(StorageEvents.clearExpired, keys);
                this._keysChanged = count > 0;
                this._doChanged();
            }
        };

        /**
         * 清理所有键
         * @private
         * @returns {void}
         */
        this._clearAll = () => {
            const keys = Array.from(this._allKeys.keys());
            const count = this._removes(keys);
            this._keysChanged = count > 0;
            if (this._keysChanged) {
                this._emit(StorageEvents.clear, keys);
                this._doChanged();
            }
        };

        /**
         * 设置键值
         * @param key {string}
         * @param value {any}
         * @param ttl {Number}
         * @private
         * @return {void}
         */
        this._set = (key, value, ttl = 0) => {
            try {
                let expire = 0;
                if (ttl > 0) {
                    expire = (new Date()).getTime() + ttl;
                }
                const data = JSON.stringify({ data: value, expire });
                const dataStoreKey = this._getDataStoreKey(key);
                const oldValue = this._get(key, null);
                if (oldValue !== value) {
                    this._storage.setItem(dataStoreKey, data);
                    this._keysChanged = oldValue !== value;
                    this._allKeys.set(key, { expire });
                    this._emit(StorageEvents.update, { key, value });
                    this._doChanged();
                }
            } catch (e) {
                console.error('[Storage] set error:', e);
            }
        };
        /**
         * 读取键值
         * @param key {string}
         * @param defValue {any}
         * @private
         * @returns {any}
         */
        this._get = (key, defValue) => {
            try {
                if (!this._allKeys.has(key)) return defValue;
                const dataStoreKey = this._getDataStoreKey(key);
                const s = this._storage.getItem(dataStoreKey);
                if (s && s.trim().length > 0) {
                    const obj = JSON.parse(s) || {};
                    const { data, expire = 0 } = obj || {};
                    const now = (new Date()).getTime();
                    if (expire > 0 && now > expire) {
                        this._remove(key);
                        return defValue;
                    }
                    return data || defValue;
                }
            } catch (e) {
                console.error('[Storage] get error:', e);
                return defValue;
            }
        };
        /**
         * 删除键
         * @param key
         * @private
         * @returns {void}
         */
        this._remove = (key) => {
            if (this._allKeys.has(key)) {
                this._allKeys.delete(key);
                const dataStoreKey = this._getDataStoreKey(key);
                this._storage.removeItem(dataStoreKey);
                this._emit(StorageEvents.delete, { key });
                this._keysChanged = true;
                this._doChanged();
            }
        };

        // private loadKeys 加载所有键名
        this._loadKeys = () => {
            try {
                const key = this._getAllKeyStoreKey();
                const s = this._storage.getItem(key);
                if (s && s.trim().length > 0) {
                    const obj = JSON.parse(s);
                    if (!obj) return;
                    const expiredKeys = new Set();
                    const nowTime = (new Date()).getTime();
                    const allKeys = new Map();
                    for (const k of Object.keys(obj)) {
                        const { expire = 0 } = obj[k] || {};
                        if (expire > 0 && nowTime > expire) {
                            expiredKeys.add(k);
                            // 不保存已过期的键
                            continue;
                        }
                        allKeys.set(k, { expire });
                    }
                    this._allKeys = allKeys;
                    if (expiredKeys.length > 0) {
                        this._removes(expiredKeys); // 清理过期的键
                    }
                    this._keysChanged = false;
                    this._emit(StorageEvents.load, { keys: allKeys });
                }
            } catch (e) {
                console.error('[Storage] loadCacheKeys error:', e);
            }
        };

        this._doChanged = () => {
            this._saveKeys();
            this._emit(StorageEvents.changed, { keys: this._allKeys });
        };

        // private stopScheduled 停止清理任务
        this._stopScheduled = () => {
            if (this._clearTimer) {
                const n = this._clearTimer;
                this._clearTimer = 0;
                clearInterval(n);
                this._emit(StorageEvents.stop, {});
            }
        };

        // private startScheduled 开启定时任务
        this._startScheduled = () => {
            this._stopScheduled();
            this._clearTimer = setInterval(this._clearExpiredKeys, this._options?.clearInterval || 10 * 60 * 60 * 1000);
            this._emit(StorageEvents.start, {});
        };

        this.init();
    }

    /**
     * 加载所有键名
     * returns {void}
     */
    loadKeys () {
        this._loadKeys();
    }

    /**
     * 初始化过程
     * @returns {(Storage)}
     */
    init () {
        this._loadKeys();
        this._startScheduled();
        return this;
    }

    /**
     * 类销毁过程，清理定时器等资源
     * @returns {void}
     */
    destroy () {
        this._stopScheduled(); // 停止定时任务
        this._clearExpiredKeys(); // 清理过期键
        this._saveKeys(); // 保存键
        super.destroy();
    }

    /**
     * 删除事件监听
     * @param listener {(function(): void)}
     * @returns {(function(): void)}
     */
    onDelete (listener) {
        return this._on(StorageEvents.delete, listener);
    }

    /**
     * 一次性监听
     * @param listener {(function():void)}
     * @returns {void}
     */
    onceDelete (listener) {
        this._once(StorageEvents.delete, listener);
    }

    /**
     * onClearExpired 绑定过期清理事件
     * @param listener {(function(): void)}
     * @return {(function(): void)}
     */
    onClearExpired (listener) {
        return this._on(StorageEvents.clearExpired, listener);
    }

    /**
     * 开启定时任务
     * @returns {void}
     */
    startScheduled () {
        if (this._clearTimer) return;
        this._startScheduled();
    }

    /**
     * 停止定时任务
     * @returns {void}
     */
    stopScheduled () {
        this._stopScheduled();
    }

    /**
     * 清理过期的数据
     * @returns {void}
     */
    clearExpire () {
        this._clearExpiredKeys();
    }

    /**
     * 清理所有数据
     * @returns {void}
     */
    clear () {
        this._clearAll();
    }

    /**
     * 保存值
     * @param key {string}
     * @param value {any}
     * @param ttl {number} 过期时长，单位毫秒
     * @returns {void}
     */
    set (key, value, ttl = 0) {
        this._set(key, value, ttl);
    }

    /**
     * 读取值
     * @param key {string}
     * @param defValue {any}
     * @returns {any}
     */
    get (key, defValue) {
        return this._get(key, defValue);
    }

    /**
     * 删除值
     * @param key
     * @returns {void}
     */
    remove (key) {
        this._remove(key);
    }

    /**
     * 检查键名是否存在
     * @param key {string}
     * @returns {boolean}
     */
    hasKey (key) {
        return this._allKeys.has(key);
    }
}

const storage = new Storage();
export { Storage, storage };
